import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services';
import './AuthPages.css';

export function ParentLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.loginParent(email, password);
      const { access_token, user } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user_type', 'parent');
      localStorage.setItem('user_id', user.id);

      navigate('/parent-dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>🦸 Missão de Herói</h1>
        <h2>Login - Responsável</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Senha:</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <p className="forgot-password-link">
          <button className="link-button" onClick={() => navigate('/forgot-password')}>
            Esqueci minha senha
          </button>
        </p>

        <p>
          Não tem conta?{' '}
          <button className="link-button" onClick={() => navigate('/parent-signup')}>
            Cadastre-se
          </button>
        </p>

        <button className="secondary-btn" onClick={() => navigate('/child-login')}>
          Sou uma criança
        </button>
      </div>
    </div>
  );
}

export function ParentSignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [cep, setCep] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ valid: false, messages: [] });
  const navigate = useNavigate();

  const handleCepChange = async (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
    setCep(value);

    // Busca dados do CEP quando tiver 8 dígitos
    if (value.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${value}/json/`);
        const data = await response.json();
        
        if (data.erro) {
          setError('CEP não encontrado');
          setCity('');
          setState('');
        } else {
          setCity(data.localidade);
          setState(data.uf);
          setError('');
        }
      } catch (err) {
        setError('Erro ao buscar CEP');
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const validatePassword = (pwd) => {
    const messages = [];
    let valid = true;

    if (pwd.length < 8) {
      messages.push('Mínimo 8 caracteres');
      valid = false;
    }
    if (!/[A-Z]/.test(pwd)) {
      messages.push('Uma letra maiúscula');
      valid = false;
    }
    if (!/[a-z]/.test(pwd)) {
      messages.push('Uma letra minúscula');
      valid = false;
    }
    if (!/[0-9]/.test(pwd)) {
      messages.push('Um número');
      valid = false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      messages.push('Um caractere especial (!@#$%^&*(),.?":{}|<>)');
      valid = false;
    }

    setPasswordStrength({ valid, messages });
    return valid;
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    if (pwd) {
      validatePassword(pwd);
    } else {
      setPasswordStrength({ valid: false, messages: [] });
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('As senhas não conferem');
      return;
    }

    if (!passwordStrength.valid) {
      setError('A senha não atende aos requisitos de segurança');
      return;
    }

    setLoading(true);

    try {
      await authService.signupParent(name, email, password, birthdate, cep, city, state);
      // Redireciona para página de verificação com o email
      navigate('/verify-email', { state: { email } });
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>🦸 Missão de Herói</h1>
        <h2>Cadastro - Responsável</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSignup}>
          <div className="form-group">
            <label>Nome:</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Data de Nascimento:</label>
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className="form-group">
            <label>CEP:</label>
            <input
              type="text"
              value={cep}
              onChange={handleCepChange}
              placeholder="00000-000"
              maxLength="8"
            />
            {loadingCep && <small>Buscando endereço...</small>}
          </div>

          <div className="form-group">
            <label>Cidade:</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Preenchido automaticamente"
            />
          </div>

          <div className="form-group">
            <label>Estado:</label>
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="UF"
              maxLength="2"
            />
          </div>

          <div className="form-group">
            <label>Senha:</label>
            <input
              type="password"
              value={password}
              onChange={handlePasswordChange}
              required
            />
            {password && (
              <div className="password-strength">
                {passwordStrength.valid ? (
                  <small style={{ color: 'green' }}>✓ Senha forte</small>
                ) : (
                  <div>
                    <small style={{ color: 'red' }}>A senha deve conter:</small>
                    <ul style={{ fontSize: '0.85em', marginTop: '5px', paddingLeft: '20px' }}>
                      {passwordStrength.messages.map((msg, idx) => (
                        <li key={idx} style={{ color: 'red' }}>• {msg}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Confirmar Senha:</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Cadastrando...' : 'Cadastrar'}
          </button>
        </form>

        <p>
          Já tem conta?{' '}
          <button className="link-button" onClick={() => navigate('/parent-login')}>
            Faça login
          </button>
        </p>
      </div>
    </div>
  );
}

export function ChildLoginPage() {
  const [pairingCode, setPairingCode] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handlePairingCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPairingCode(value);
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (pairingCode.length !== 6) {
      setError('Código de pareamento deve ter 6 dígitos');
      return;
    }

    if (pin.length !== 4) {
      setError('PIN deve ter 4 dígitos');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.loginChild(pairingCode, pin);
      const { access_token, child } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user_type', 'child');
      localStorage.setItem('user_id', child.id);

      navigate('/child-dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Código ou PIN inválido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>🦸 Missão de Herói</h1>
        <h2>Login - Criança</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Código de Pareamento (6 dígitos):</label>
            <input
              type="text"
              value={pairingCode}
              onChange={handlePairingCodeChange}
              placeholder="000000"
              maxLength="6"
              required
              autoFocus
            />
            <small style={{ color: '#666', marginTop: '4px' }}>Peça este código ao responsável</small>
          </div>

          <div className="form-group">
            <label>PIN (4 dígitos):</label>
            <input
              type="text"
              value={pin}
              onChange={handlePinChange}
              placeholder="0000"
              maxLength="4"
              required
            />
            <small style={{ color: '#666', marginTop: '4px' }}>Seu PIN pessoal</small>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <button className="secondary-btn" onClick={() => navigate('/parent-login')}>
          Sou responsável
        </button>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authService.forgotPassword(email);
      setSuccess('Código enviado! Verifique seu email.');
      setTimeout(() => {
        navigate('/reset-password', { state: { email } });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao solicitar redefinição');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>🦸 Missão de Herói</h1>
        <h2>Esqueci minha senha</h2>

        <p className="info-text">
          Digite seu email e enviaremos um código para redefinir sua senha.
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Enviando...' : 'Enviar código'}
          </button>
        </form>

        <p>
          Lembrou sua senha?{' '}
          <button className="link-button" onClick={() => navigate('/parent-login')}>
            Voltar ao login
          </button>
        </p>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = window.location;

  React.useEffect(() => {
    // Pega email da navegação se disponível
    const state = location.state;
    if (state?.email) {
      setEmail(state.email);
    }
  }, [location]);

  const handleResetCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setResetCode(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem');
      return;
    }

    if (newPassword.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(email, resetCode, newPassword);
      setSuccess('Senha redefinida com sucesso!');
      setTimeout(() => {
        navigate('/parent-login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>🦸 Missão de Herói</h1>
        <h2>Redefinir senha</h2>

        <p className="info-text">
          Digite o código que você recebeu por email e sua nova senha.
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Código de verificação:</label>
            <input
              type="text"
              value={resetCode}
              onChange={handleResetCodeChange}
              placeholder="000000"
              maxLength="6"
              required
              style={{ fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' }}
            />
            <small style={{ color: '#666', marginTop: '4px' }}>
              Código de 6 dígitos enviado por email
            </small>
          </div>

          <div className="form-group">
            <label>Nova senha:</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength="6"
            />
          </div>

          <div className="form-group">
            <label>Confirmar nova senha:</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength="6"
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Redefinindo...' : 'Redefinir senha'}
          </button>
        </form>

        <p>
          <button className="link-button" onClick={() => navigate('/parent-login')}>
            Voltar ao login
          </button>
        </p>
      </div>
    </div>
  );
}

export function VerifyEmailPage() {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pega email do state ou da query string
  const queryParams = new URLSearchParams(location.search);
  const emailFromQuery = queryParams.get('email');
  const email = location.state?.email || emailFromQuery;

  // Se não tiver email, redireciona para signup
  React.useEffect(() => {
    if (!email) {
      navigate('/parent-signup');
    }
  }, [email, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authService.verifyEmail(email, verificationCode);
      setSuccess('Email verificado com sucesso! Redirecionando para login...');
      setTimeout(() => {
        navigate('/parent-login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>🦸 Missão de Herói</h1>
        <h2>Verificar Email</h2>

        <p className="info-message">
          Enviamos um código de 6 dígitos para <strong>{email}</strong>
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleVerify}>
          <div className="form-group">
            <label>Código de Verificação:</label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength="6"
              required
              autoFocus
              style={{ fontSize: '24px', textAlign: 'center', letterSpacing: '8px' }}
            />
            <small>Digite o código de 6 dígitos recebido por email</small>
          </div>

          <button type="submit" disabled={loading || verificationCode.length !== 6}>
            {loading ? 'Verificando...' : 'Verificar'}
          </button>
        </form>

        <p>
          Não recebeu o código?{' '}
          <button className="link-button" onClick={() => {/* TODO: Reenviar código */}}>
            Reenviar
          </button>
        </p>

        <p>
          <button className="link-button" onClick={() => navigate('/parent-signup')}>
            Voltar ao cadastro
          </button>
        </p>
      </div>
    </div>
  );
}
